import {
    componentClasses,
    componentName,
    defaultProps,
    eventMap,
    schema,
    views,
} from "../../../core/components/drawer/drawer";
import { chevronLeft } from "../../../core/js/helpers/built-in-icons";
import validateInitialization from "../../../core/js/helpers/validate-initialization";
import {
    actions,
    createStore,
    dispatchEvent,
    reducers,
    renderState,
} from "../../../core/js/state-machine";
import {
    createCSSClass,
    createElement,
    getFocusableElements,
    prefix,
} from "../../../core/js/utilities";

const validateHTML = (element) => {
    const overlayElement = element.querySelector(`.${componentClasses.overlay.base}`);
    const headerElement = element.querySelector(`.${componentClasses.header}`);
    const containerElement = element.querySelector(`.${componentClasses.container}`);

    return Boolean(overlayElement && headerElement && containerElement);
};

export default function Drawer(element, props) {
    const [success, mergedProps] = validateInitialization({
        componentName,
        element,
        defaultProps,
        props,
        views,
        schema,
        validateHTML,
    });

    if (!success) return;

    const setTriggers = () => {
        // Using try/catch because trigger property is a user input and a bad selector can result in errors
        try {
            triggers = document.querySelectorAll(mergedProps.trigger);
            triggers.forEach((trigger) => {
                trigger.addEventListener("click", toggle);
            });
        } catch (error) {
            console.error(error);
        }
    };

    const unsetTriggers = () => {
        triggers.forEach((trigger) => {
            trigger.removeEventListener("click", toggle);
        });

        triggers = [];
    };

    const setCustomWidth = () => {
        const widthDynamicClass = `${prefix}dynamic-drawer-w-${mergedProps.width}`;

        if (createCSSClass(`.${widthDynamicClass}`, { width: `${mergedProps.width} !important` }))
            renderState({ classList: [widthDynamicClass] }, containerElement);
    };

    const handleElementKeydown = (e) => {
        // ESC
        if (e.key === "Escape") {
            close();
        }
    };

    const buildCloseIcon = () => {
        closeElement.insertAdjacentHTML("afterbegin", chevronLeft());
    };

    const focusFirstElement = () => {
        const focusableElements = getFocusableElements(containerElement);
        if (focusableElements && focusableElements.length > 0) {
            // Use a small setTimeout to allow the animation to be ready before we can focus the element.
            setTimeout(() => {
                focusableElements[0].focus && focusableElements[0].focus();
            }, 100);
        }
    };

    const focusLastElement = () => {
        const focusableElements = getFocusableElements(containerElement);
        if (focusableElements && focusableElements.length > 0)
            focusableElements[focusableElements.length - 1].focus &&
                focusableElements[focusableElements.length - 1].focus();
    };

    /**
     * Our drawer component should be constrained to only tab within itself when it is open.
     */
    const buildFocusTrap = () => {
        startFocusTrapElement = createElement("span", {
            "aria-hidden": true,
            tabindex: 0,
        });

        endFocusTrapElement = createElement("span", {
            "aria-hidden": true,
            tabindex: 0,
        });

        startFocusTrapElement.addEventListener("focus", focusLastElement);
        endFocusTrapElement.addEventListener("focus", focusFirstElement);

        containerElement.insertBefore(startFocusTrapElement, headerElement);
        containerElement.appendChild(endFocusTrapElement);
    };

    /**
     * When our transition has finished focusing, we must place our focus.
     * For opening, we should focus the first element of our drawer (back button)
     * For closing, we should go back to our previous focused item that opened the drawer (if exists).
     * @param {Event} e
     */
    const handleBodyTransitionEnd = (e) => {
        // Event transitionend works bubbling, that means any child element with transition will trigger this event
        // That is why we need to verify the target and the property to only execute the focus on focusFirstElement
        // if we are dealing with the drawer transition and not from his childs
        if (e.target !== containerElement || e.propertyName !== "transform") return;

        const visible = visibleStore.getState();
        dispatchEvent(visible ? eventMap.opened : eventMap.closed, element);
        if (visible) {
            focusFirstElement();
        } else {
            previousFocusedItem && previousFocusedItem.focus && previousFocusedItem.focus();
            previousFocusedItem = null;
        }
    };

    const handleVisibleStateChange = (visible) => {
        if (visible) previousFocusedItem = document.activeElement;
        renderState(visible ? views.visible : views.hidden, element);
        renderState(visible ? views.visible : views.hidden, overlayElement);
        renderState(visible ? views.body.overflowHidden : views.body.base, document.body);
        dispatchEvent(visible ? eventMap.open : eventMap.close, element);
    };

    const close = () => {
        return visibleStore.dispatch(actions.close());
    };

    const open = () => {
        return visibleStore.dispatch(actions.open());
    };

    const toggle = () => {
        return visibleStore.dispatch(actions.toggle());
    };

    const dispose = function () {
        if (closeElement) closeElement.removeEventListener("click", close);
        if (overlayElement) overlayElement.removeEventListener("click", open);
        if (mergedProps.trigger) unsetTriggers();
        containerElement.removeEventListener("transitionend", handleBodyTransitionEnd);
        startFocusTrapElement.removeEventListener("focus", focusLastElement);
        endFocusTrapElement.removeEventListener("focus", focusFirstElement);
        element.removeEventListener("keydown", handleElementKeydown);
        element[componentName] = null;
        element = null;
        api = null;
    };

    // Used to save the previous focused item to give the focus back once drawer close
    let previousFocusedItem = null;

    const closeElement = element.querySelector(`.${componentClasses.close}`);
    if (closeElement) {
        buildCloseIcon();
        closeElement.addEventListener("click", close);
    }

    const overlayElement = element.querySelector(`.${componentClasses.overlay.base}`);
    const headerElement = element.querySelector(`.${componentClasses.header}`);
    const containerElement = element.querySelector(`.${componentClasses.container}`);

    overlayElement.addEventListener("click", close);
    containerElement.addEventListener("transitionend", handleBodyTransitionEnd);

    let startFocusTrapElement = null;
    let endFocusTrapElement = null;
    buildFocusTrap();

    element.addEventListener("keydown", handleElementKeydown);

    // Set triggers, storying globally so it can be disposed
    let triggers = [];
    if (mergedProps.trigger) setTriggers();
    if (mergedProps.width) setCustomWidth();

    // Execute state change with initial visible prop in case component start visible
    if (mergedProps.visible) handleVisibleStateChange(mergedProps.visible);
    const visibleStore = createStore(reducers.boolReducer, mergedProps.visible);
    visibleStore.subscribe(handleVisibleStateChange);

    let api = {
        element,
        dispose,
        close,
        open,
        toggle,
    };

    element[componentName] = api;

    return api;
}
